import { computed, Injectable, signal } from '@angular/core';

export type ThemeMode = 'light' | 'dark' | 'auto';

const STORAGE_KEY = 'mg3p-theme';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly systemPrefersDark = signal(this.readSystemDark());

  readonly mode = signal<ThemeMode>(this.readStored());

  readonly effectiveDark = computed(() => {
    const m = this.mode();
    if (m === 'dark') {
      return true;
    }
    if (m === 'light') {
      return false;
    }
    return this.systemPrefersDark();
  });

  readonly modeLabel = computed(() => {
    const labels: Record<ThemeMode, string> = {
      light: 'Claro',
      dark: 'Escuro',
      auto: 'Automático',
    };
    return labels[this.mode()];
  });

  constructor() {
    this.apply(this.mode());

    if (typeof window !== 'undefined' && window.matchMedia) {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      const onChange = (): void => {
        this.systemPrefersDark.set(mq.matches);
        if (this.mode() === 'auto') {
          this.apply('auto');
        }
      };
      mq.addEventListener('change', onChange);
    }
  }

  cycle(): void {
    const order: ThemeMode[] = ['light', 'dark', 'auto'];
    const idx = order.indexOf(this.mode());
    const next = order[(idx + 1) % order.length];
    this.setMode(next);
  }

  setMode(mode: ThemeMode): void {
    this.mode.set(mode);
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      /* storage indisponível */
    }
    this.apply(mode);
  }

  private apply(mode: ThemeMode): void {
    if (typeof document === 'undefined') {
      return;
    }
    document.documentElement.setAttribute('data-theme', mode);
    document.documentElement.style.colorScheme =
      mode === 'dark' ? 'dark' : mode === 'light' ? 'light' : '';
  }

  private readStored(): ThemeMode {
    try {
      const v = localStorage.getItem(STORAGE_KEY);
      if (v === 'light' || v === 'dark' || v === 'auto') {
        return v;
      }
    } catch {
      /* ignore */
    }
    return 'auto';
  }

  private readSystemDark(): boolean {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return false;
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }
}
